/**
 * Entity Filters Component
 * Reusable filter UI for entity tables
 */

import React from 'react';
import { Filter } from 'lucide-react';
import { detectFieldType, getFilterOptions } from '../../utils/entityFieldFormatter';

export interface EntityDetail {
  id: string;
  data: any;
  source_artifact_id?: string;
}

interface EntityFiltersProps {
  entities: EntityDetail[];
  fields: string[];
  filters: Record<string, any>;
  onFilterChange: (column: string, value: any) => void;
  maxVisibleFields?: number;
}

export function EntityFilters({
  entities,
  fields,
  filters,
  onFilterChange,
  maxVisibleFields = 12,
}: EntityFiltersProps) {
  if (fields.length === 0) return null;

  const visibleFields = fields.slice(0, maxVisibleFields);

  return (
    <div className="bg-theme-elevated border border-theme-border rounded-lg p-4 flex-shrink-0">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {visibleFields.map((key) => {
          const sampleValue = entities.find(e => e.data?.[key] !== null && e.data?.[key] !== undefined)?.data?.[key];
          const fieldMeta = sampleValue !== undefined ? detectFieldType(key, sampleValue) : null;
          const filterOptions = fieldMeta ? getFilterOptions(entities, key) : null;

          if (!filterOptions || filterOptions.options.length === 0) return null;

          return (
            <div key={key} className="flex flex-col">
              <label className="text-xs font-medium text-theme-text mb-1">
                {key.replace(/_/g, ' ').substring(0, 20)}
              </label>
              {filterOptions.type === 'number' && filterOptions.options.length > 0 ? (
                <select
                  value={filters[key]?.label || 'all'}
                  onChange={(e) => {
                    const selected = filterOptions.options.find((opt: any) => opt.label === e.target.value);
                    onFilterChange(key, selected || null);
                  }}
                  className="w-full px-2 py-1.5 text-xs border border-theme-border rounded bg-theme-paper text-theme-text"
                >
                  <option value="all">All</option>
                  {filterOptions.options.map((opt: any, idx: number) => (
                    <option key={idx} value={opt.label}>{opt.label}</option>
                  ))}
                </select>
              ) : filterOptions.type === 'boolean' ? (
                <select
                  value={filters[key] || 'all'}
                  onChange={(e) => onFilterChange(key, e.target.value === 'all' ? null : e.target.value === 'true')}
                  className="w-full px-2 py-1.5 text-xs border border-theme-border rounded bg-theme-paper text-theme-text"
                >
                  <option value="all">All</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              ) : filterOptions.options.length > 0 && filterOptions.options.length <= 20 ? (
                <select
                  value={filters[key] || 'all'}
                  onChange={(e) => onFilterChange(key, e.target.value === 'all' ? null : e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-theme-border rounded bg-theme-paper text-theme-text"
                >
                  <option value="all">All</option>
                  {filterOptions.options.map((opt: string, idx: number) => (
                    <option key={idx} value={opt}>{opt}</option>
                  ))}
                </select>
              ) : null}
            </div>
          );
        })}
      </div>
      {Object.keys(filters).length > 0 && (
        <button
          onClick={() => {
            Object.keys(filters).forEach(key => onFilterChange(key, null));
          }}
          className="mt-3 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded"
        >
          Clear All Filters
        </button>
      )}
    </div>
  );
}











